import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Toggle } from '@/components/ui/primitives'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { t } from '@/i18n'

interface TaxSettings {
  vatEnabled: boolean
  defaultVatRate: number
  vatNumber: string
  vatMode: 'Inclusive' | 'Exclusive'
}

export function VatSection() {
  const queryClient = useQueryClient()
  const canManage = useSession((state) => state.can)(Permission.ManageSettings)

  const [enabled, setEnabled] = useState(true)
  const [percent, setPercent] = useState('11')
  const [number, setNumber] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const settings = useQuery({
    queryKey: ['settings', 'tax'],
    queryFn: () => apiClient.get<TaxSettings>('/api/settings/tax'),
  })

  useEffect(() => {
    if (settings.data) {
      setEnabled(settings.data.vatEnabled)
      // Stored as a fraction, shown as a percentage — that is how a shopkeeper thinks of it.
      setPercent(String(settings.data.defaultVatRate * 100))
      setNumber(settings.data.vatNumber ?? '')
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: () =>
      apiClient.put('/api/settings/tax', {
        vatEnabled: enabled,
        vatRate: Number(percent || 0) / 100,
        vatNumber: number.trim() || null,
      }),
    onSuccess: async () => {
      setError(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      await queryClient.invalidateQueries({ queryKey: ['settings'] })
      await queryClient.invalidateQueries({ queryKey: ['checkout'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  return (
    <div>
      <p className="mb-4 text-sm text-[var(--text-secondary)]">
        Lebanon's standard rate is 11%. A business below the registration threshold should switch
        VAT off — no tax is then charged, and nothing about VAT appears on a receipt.
      </p>

      <div className="surface-raised rounded-[var(--radius-card)] border p-4">
        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label={t('This shop charges VAT')}
          hint={
            enabled
              ? 'Tax is calculated on every sale and broken out on the receipt'
              : 'Sales charge no tax, and receipts show no VAT line at all'
          }
        />

        {enabled && (
          <div className="mt-4 flex flex-col gap-4 border-t border-[var(--border-subtle)] pt-4">
            <Input
              label={t('Rate (%)')}
              type="number"
              inputMode="decimal"
              step="0.5"
              min="0.01"
              max="99"
              value={percent}
              onChange={(event) => setPercent(event.target.value)}
              className="tabular text-lg"
              hint={t('Applies to new products. Individual products can still be zero-rated, like bread.')}
            />
            <Input
              label={t('VAT registration number')}
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              placeholder={t('Printed on receipts')}
            />
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 border-t border-[var(--border-subtle)] pt-4">
          <span className="text-sm text-[var(--text-secondary)]">{t('Prices are')}</span>
          <Badge tone="neutral">
            {settings.data?.vatMode === 'Exclusive' ? 'VAT added at checkout' : 'VAT included'}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          Set when your store was created. Changing it would reprice the whole catalogue, so it
          is not editable here — contact your provider if it is wrong.
        </p>

        {error && (
          <p role="alert" className="mt-4 flex items-start gap-2 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}

        {canManage ? (
          <Button className="mt-4" full onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save VAT settings'}
          </Button>
        ) : (
          <p className="surface-sunken mt-4 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)]">
            {t('Only an administrator can change VAT settings.')}
          </p>
        )}
      </div>

      <p className="mt-4 text-xs text-[var(--text-secondary)]">
        Switching VAT off does not rewrite past sales. A receipt reprinted from before the change
        still shows the tax the customer actually paid.
      </p>
    </div>
  )
}
